import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { PromptItem } from './PromptItem';
import { PromptData } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    headerWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '16px',
    },
    header: {
      flex: 1,
      minWidth: '300px',
    },
    title: {
      fontWeight: 600,
      fontSize: '16px',
      marginBottom: '8px',
      color: theme.palette.text.primary,
    },
    description: {
      fontSize: '14px',
      color: theme.palette.text.secondary,
      lineHeight: '20px',
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    toggleLabel: {
      fontSize: '14px',
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
    },
    toggleSwitch: {
      position: 'relative',
      width: '48px',
      height: '24px',
      backgroundColor: theme.palette.action.disabledBackground,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      border: 'none',
      padding: 0,
      '&:focus': {
        outline: '2px solid #003F2D',
        outlineOffset: '2px',
      },
    },
    toggleSwitchActive: {
      backgroundColor: '#003F2D',
    },
    toggleThumb: {
      position: 'absolute',
      top: '2px',
      left: '2px',
      width: '20px',
      height: '20px',
      backgroundColor: theme.palette.background.paper,
      borderRadius: '50%',
      transition: 'transform 0.3s',
    },
    items: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
  }),
);

export interface PromptListProps {
  prompts: PromptData[];
  title?: string;
  description?: string;
  showDomainToggle?: boolean;
  defaultDomain?: 'all' | 'my';
  onCopyPrompt?: (id: string) => void;
  onAddPrompt?: (id: string, promptText: string) => void;
  onDomainChange?: (domain: 'all' | 'my') => void;
  className?: string;
}

export const PromptList: React.FC<PromptListProps> = ({
  prompts,
  title = 'Suggested Optimized Prompts',
  description = 'Based on repeated user patterns, here are optimized prompts your team can use',
  showDomainToggle = true,
  defaultDomain = 'all',
  onCopyPrompt,
  onAddPrompt,
  onDomainChange,
  className = '',
}) => {
  const classes = useStyles();
  const [selectedDomain, setSelectedDomain] = useState<'all' | 'my'>(defaultDomain);

  const handleDomainToggle = () => {
    const newDomain = selectedDomain === 'all' ? 'my' : 'all';
    setSelectedDomain(newDomain);
    if (onDomainChange) {
      onDomainChange(newDomain);
    }
  };

  return (
    <Box className={`${classes.container} ${className}`}>
      <Box className={classes.headerWrapper}>
        <Box className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.description}>{description}</Typography>
        </Box>

        {showDomainToggle && (
          <Box className={classes.toggle}>
            <Typography className={classes.toggleLabel}>All Domains</Typography>
            <button
              className={`${classes.toggleSwitch} ${
                selectedDomain === 'my' ? classes.toggleSwitchActive : ''
              }`}
              onClick={handleDomainToggle}
              aria-label={`Switch to ${selectedDomain === 'all' ? 'My Domain(s)' : 'All Domains'}`}
              type="button"
            >
              <Box
                className={classes.toggleThumb}
                style={{
                  transform: selectedDomain === 'my' ? 'translateX(24px)' : 'translateX(0)',
                }}
              />
            </button>
            <Typography className={classes.toggleLabel}>My Domain(s)</Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.items}>
        {(prompts ?? []).map((prompt) => (
          <PromptItem
            key={prompt.id}
            {...prompt}
            onCopy={onCopyPrompt}
            onAddPrompt={onAddPrompt}
          />
        ))}
      </Box>
    </Box>
  );
};
